import { Injectable, Injector } from '@angular/core';
import { fabric } from 'fabric';
import { FrameItem } from '../../../../models/session';
import { BoardItemsStackingOrderManager } from '../board-items-stacking-order-manager';
import { SpaceRepository } from '../../../../state/space.repository';
import { SessionsVptService } from '../../../../services/sessions-vpt.service';
import { UserService } from '../../../../services/user.service';
import { SpaceBoardsService } from '../../../../services/space-boards.service';
import { ItemModel } from '../../../../models/itemModel';
import { ResourceItemModel } from '../../../../models/resource';
import { AppConfigurationService } from '../../iframe/additional-apps.utils';
import { YObject } from '../../../common/y-session';
import { BoardFreeSpaceFinder } from '../board-free-space-finder.service';
import { FlagsService, FLAGS } from '../../../../services/flags.service';
import { isLineOrArrowShape } from '../../wb-canvas/fabric-utils';
import { CreationContext } from './board-item-configurations';
import { BoardItem } from './board.item';
import {
  itemConfigConstructors,
  Configurations,
  ImplementedItemsTypes,
  ItemModelToBoardItemsMap,
  ItemModelToConfigurationsMap,
} from './board-item-configurations-factory';
import { BoardWebViewer } from './board-web-viewer';
import { BoardPdf } from './board-pdf';
import { BoardFormula } from './board-formula';
import { BoardVideo } from './board-video';
import { BoardRemoteVideo } from './board-remote-video';
import { BoardChatGpt } from './board-chat-gpt';
import { BoardQuestion } from './board-question';
import { BoardMario } from './board-mario';
import { BoardIframe } from './board-iframe';
import { BoardItemsStaggeringService } from './board-items-staggering.service';
import { BoardStickyNote } from './board-sticky-note';
import { BoardImage } from './board-image';
import { BoardImagesPdf } from './board-images-pdf';
import { BoardImagePdf } from './board-image-pdf';
import { BoardLine } from './board-line';

/**
 * Factory service responsible for creating and managing board items.
 * This class serves as the central point for instantiating different types of board items
 * and handles the creation context and configuration setup.
 *
 * Key responsibilities:
 * - Creating board items from various sources (FrameItem, FabricObject)
 * - Managing the mapping between item types and their implementations
 * - Handling creation context and configuration initialization
 * - Coordinating with other services for proper item setup
 *
 * The factory supports multiple board item types including:
 * - WebViewer, PDF, Formula
 * - Image, Video, RemoteVideo
 * - ChatGPT, Question, Note
 * - Mario, IFrame, StickyNote
 */
@Injectable({
  providedIn: 'root',
})
export class BoardItemsFactory {
  boardItemsConstructors = {
    [ItemModel.WebViewer]: BoardWebViewer,
    [ItemModel.PDF]: BoardPdf,
    [ItemModel.Formula]: BoardFormula,
    [ItemModel.Image]: BoardImage,
    [ItemModel.Video]: BoardVideo,
    [ItemModel.RemoteVideo]: BoardRemoteVideo,
    [ItemModel.Chat_GPT]: BoardChatGpt,
    [ItemModel.Question]: BoardQuestion,
    [ItemModel.Note]: BoardQuestion,
    [ItemModel.Mario]: BoardMario,
    [ItemModel.IFrame]: BoardIframe,
    [ItemModel.StickyNote]: BoardStickyNote,
    [ItemModel.Line]: BoardLine,
    [ItemModel.PDFImages]: BoardImagesPdf,
    [ItemModel.PDFImage]: BoardImagePdf,
  };

  constructor(
    private injector: Injector,
    private boardItemsStackingOrderManager: BoardItemsStackingOrderManager,
    private spaceRepo: SpaceRepository,
    private sessionVptService: SessionsVptService,
    private userService: UserService,
    private spaceBoardService: SpaceBoardsService,
    private appConfigurationService: AppConfigurationService,
    private boardItemsStaggeringService: BoardItemsStaggeringService,
    private boardFreeSpaceFinder: BoardFreeSpaceFinder,
    private flagsService: FlagsService,
  ) {}

  public createBoardItemFromFrameItem(frameItem: FrameItem, boardUid: string): BoardItem {
    const creationContext = this.getCreationContext({ currentBoardId: boardUid });

    // This cast should be removed when all items types are implemented
    const type = this.getItemModelFromFrameItem(frameItem) as ImplementedItemsTypes;

    const constructor = itemConfigConstructors[type];

    const boardItemConfigurations = constructor.fromYCanvasItemObject(
      frameItem,
      creationContext,
    ) as Configurations;

    const boardItemsConstructor = this.boardItemsConstructors[type];

    return new boardItemsConstructor(this.injector, boardItemConfigurations, creationContext);
  }

  public createBoardItemFromFabricObject(fabricObject: YObject, boardUid: string): BoardItem {
    const creationContext = this.getCreationContext({ currentBoardId: boardUid });

    // This cast should be removed when all items types are implemented
    const type = this.getItemModelFromFabricObject(fabricObject) as ImplementedItemsTypes;

    const constructor = itemConfigConstructors[type];

    const boardItemConfigurations = constructor.fromYObject(
      fabricObject,
      creationContext,
    ) as Configurations;

    const boardItemsConstructor = this.boardItemsConstructors[type];

    return new boardItemsConstructor(this.injector, boardItemConfigurations, creationContext);
  }

  public createBoardItem<T extends keyof ItemModelToConfigurationsMap>(
    type: ImplementedItemsTypes,
    boardItemConfiguration: ItemModelToConfigurationsMap[T],
    creationContext?: Partial<CreationContext>,
  ): ItemModelToBoardItemsMap[T] {
    const fullCreationContext = this.getCreationContext(creationContext ?? {});

    const constructor = itemConfigConstructors[type];

    const fullBoardItemConfiguration = new constructor(
      boardItemConfiguration,
      fullCreationContext,
    ) as Configurations;

    const boardItemsConstructor = this.boardItemsConstructors[type];

    return new boardItemsConstructor(
      this.injector,
      fullBoardItemConfiguration,
      fullCreationContext,
    ) as ItemModelToBoardItemsMap[T];
  }

  getCreationContext(providedCreationContext: Partial<CreationContext>): CreationContext {
    const centerCoords = this.sessionVptService.getCenterCoords();
    return {
      centerXCoordinate: centerCoords.x,
      centerYCoordinate: centerCoords.y,
      currentUserId: this.userService.userId ?? '',
      maxZIndexOnTheBoard: this.boardItemsStackingOrderManager.generateMaxZIndex(),
      currentBoardId: this.spaceBoardService.getActiveSpaceSelectedBoard()?.uid ?? '',
      spaceIdToRenderIn:
        providedCreationContext.spaceIdToRenderIn ?? this.spaceRepo.activeSpaceId ?? '',
      appConfigurationService: this.appConfigurationService,
      boardItemsStaggeringService: this.boardItemsStaggeringService,
      renderInNearestFreeSpace:
        this.flagsService.isFlagEnabled(FLAGS.INSERT_APPS_IN_FREE_SPACES) &&
        (providedCreationContext.renderInNearestFreeSpace ?? false),
      boardFreeSpaceFinder: this.boardFreeSpaceFinder,
      isNewlyCreatedItem: providedCreationContext.isNewlyCreatedItem ?? false,
      featureFlagsService: this.flagsService,
    };
  }

  private getItemModelFromFrameItem(frameItem: FrameItem): ItemModel | undefined {
    const model = frameItem.model;

    if (model === ItemModel.Resource && frameItem.resource_state?.resource_item_model) {
      return resourceItemModelToItemModel(frameItem.resource_state.resource_item_model);
    }

    return model;
  }

  private getItemModelFromFabricObject(fabricObject: fabric.Object): ItemModel | undefined {
    if (fabricObject['customObjectType'] === 'sticky-note') {
      return ItemModel.StickyNote;
    }
    if (isLineOrArrowShape(fabricObject)) {
      return ItemModel.Line;
    }
    return undefined;
  }
}

export const resourceItemModelToItemModel = (
  resourceItemModel: ResourceItemModel,
): ItemModel.PDF | ItemModel.PDFImages | ItemModel.Video | ItemModel.Image =>
  ({
    [ResourceItemModel.DOCUMENT]: ItemModel.PDF,
    [ResourceItemModel.PDF]: ItemModel.PDF,
    [ResourceItemModel.IMAGE]: ItemModel.Image,
    [ResourceItemModel.VIDEO]: ItemModel.Video,
    [ResourceItemModel.PDF_IMAGES]: ItemModel.PDFImages,
  }[resourceItemModel]);
